import { c as _c } from "react/compiler-runtime";
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $wrapNodes } from '@lexical/selection';
import { Select, Combobox as Combo } from '@mantine/core';
import { $createParagraphNode, $getSelection, $isRangeSelection, ElementNode, LexicalEditor, RangeSelection } from 'lexical';
import { match } from 'ts-pattern';
import { entries, mapEntries } from 'shared/collection';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { $createCodeNode } from '@lexical/code';
import { useEffect, useMemo } from 'react';
import { splitNodeContainingQuery, useTriggerMatchedCombo } from './triggerMatch';
const {
  debug: consoleDebug
} = console;
export const blockTypes = {
  paragraph: 'Text',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  bullet: 'Bulleted list',
  number: 'Numbered list',
  quote: 'Quote',
  code: 'Code'
};
export type BlockType = keyof typeof blockTypes;
export const BlockSelect = t0 => {
  const $ = _c(8);
  const {
    blockType,
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      input: "!border-0"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = mapEntries(blockTypes, _temp);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== blockType || $[3] !== editor) {
    t3 = type => {
      if (blockType == type) {
        return;
      }
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          makeNode({
            selection,
            type: type as BlockType,
            editor
          });
        }
      });
    };
    $[2] = blockType;
    $[3] = editor;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== blockType || $[6] !== t3) {
    t4 = <Select maxDropdownHeight={400} className="w-[11em]" classNames={t1} value={blockType} data={t2} onChange={t3} />;
    $[5] = blockType;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function makeNode({
  selection,
  type,
  editor
}: {
  selection: RangeSelection;
  type: BlockType;
  editor: LexicalEditor;
}) {
  const wrap = (createEl: () => ElementNode) => {
    if (selection) $wrapNodes(selection, () => createEl());
  };
  match(type).with('h1', 'h2', 'h3', h => wrap(() => $createHeadingNode(h))).with('paragraph', () => wrap(() => $createParagraphNode())).with('quote', () => wrap(() => $createQuoteNode())).with('number', () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)).with('bullet', () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined))
  // .with('check', () => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined))
  .with('code', () => {
    if (selection.isCollapsed()) {
      $wrapNodes(selection, () => $createCodeNode());
    } else {
      const textContent = selection.getTextContent();
      const codeNode = $createCodeNode();
      selection.removeText();
      selection.insertNodes([codeNode]);
      selection.insertRawText(textContent);
    }
  }).run();
}
const blockItems = entries(blockTypes).map(([v, l]) => ({
  value: v,
  label: l
}));
export const BlockSelectPlugin = t0 => {
  const $ = _c(14);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      trigger: "/"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    targetDivProps,
    store,
    editor,
    triggerMatch,
    readonly
  } = useTriggerMatchedCombo(t1);
  const matchingString = triggerMatch?.match.matchingString;
  let t2;
  if ($[1] !== matchingString) {
    t2 = blockItems.filter(i => matchingString == null || i.label.toLowerCase().includes(matchingString.toLowerCase().trim()));
    $[1] = matchingString;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const options = t2;
  let t3;
  if ($[3] !== store) {
    t3 = () => {
      store.selectFirstOption();
    };
    $[3] = store;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== options) {
    t4 = [options];
    $[5] = options;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== editor || $[8] !== options || $[9] !== readonly || $[10] !== store || $[11] !== targetDivProps || $[12] !== triggerMatch) {
    t5 = readonly ? <></> : <Combo withinPortal={true} store={store} onOptionSubmit={type => {
      if (!type) {
        return;
      }
      editor.update(() => {
        const selection = $getSelection();
        const isRange = $isRangeSelection(selection);
        if (!isRange || triggerMatch?.match == null) {
          return;
        }
        const textNodeContainingQuery = splitNodeContainingQuery(triggerMatch.match);
        if (textNodeContainingQuery) {
          const topNode = textNodeContainingQuery.getTopLevelElementOrThrow();
          textNodeContainingQuery.remove();
          topNode.getChildrenSize() > 0 ? topNode.insertAfter($createParagraphNode()) : topNode;
          makeNode({
            type: type as BlockType,
            selection,
            editor
          });
        }
      });
    }}><Combo.Target><div {...targetDivProps} /></Combo.Target><Combo.Dropdown><Combo.Options>{options.map(_temp2)}</Combo.Options></Combo.Dropdown></Combo>;
    $[7] = editor;
    $[8] = options;
    $[9] = readonly;
    $[10] = store;
    $[11] = targetDivProps;
    $[12] = triggerMatch;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
function _temp(value, label) {
  return {
    label,
    value
  };
}
function _temp2(i_0) {
  return <Combo.Option value={i_0.value} key={i_0.value}>{i_0.label}</Combo.Option>;
}